import useSocialLogin, {
	isUnverifiedEmailHeldConflict,
	useResolveSocialConflict,
} from "api/UserResource/useSocialLogin";
import SocialEmailConflictDialog from "components/v2/Auth/SocialEmailConflictDialog";
import useFacebookSdk from "hooks/useFacebookSdk";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaFacebook } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import toast from "react-hot-toast";
import { useCallback, useEffect, useState } from "react";

/**
 * Google Sign-In via GIS ID token (credential JWT).
 * The backend verifies this token against Google's published signing keys.
 */
const GoogleSocialButton = ({ disabled, onIdToken }) => {
	const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
	const [gisReady, setGisReady] = useState(
		Boolean(typeof window !== "undefined" && window.google?.accounts?.id),
	);

	useEffect(() => {
		if (!clientId || typeof window === "undefined") {
			return;
		}

		let cancelled = false;
		let tries = 0;

		const waitForGis = () => {
			if (cancelled) return;
			if (window.google?.accounts?.id) {
				setGisReady(true);
				return;
			}
			tries += 1;
			if (tries < 40) {
				window.setTimeout(waitForGis, 100);
			}
		};

		waitForGis();

		return () => {
			cancelled = true;
		};
	}, [clientId]);

	const handleClick = useCallback(() => {
		if (!clientId) {
			toast.error("Google login is not configured");
			return;
		}

		if (!window.google?.accounts?.id) {
			toast.error("Google Sign-In failed to load. Please refresh and try again.");
			return;
		}

		window.google.accounts.id.initialize({
			client_id: clientId,
			callback: async (response) => {
				if (!response?.credential) {
					toast.error("Google did not return an ID token");
					return;
				}
				await onIdToken(response.credential);
			},
			auto_select: false,
			cancel_on_tap_outside: true,
		});

		// Prefer the account chooser prompt; if One Tap is blocked, fall back
		// to rendering a temporary official button and clicking it.
		window.google.accounts.id.prompt((notification) => {
			if (
				notification.isNotDisplayed() ||
				notification.isSkippedMoment() ||
				notification.isDismissedMoment()
			) {
				const host = document.createElement("div");
				host.style.position = "fixed";
				host.style.left = "-9999px";
				host.style.top = "0";
				document.body.appendChild(host);

				window.google.accounts.id.renderButton(host, {
					type: "standard",
					theme: "outline",
					size: "large",
					text: "continue_with",
					width: 320,
				});

				const btn = host.querySelector("div[role='button']") || host.querySelector("div");
				if (btn) {
					btn.click();
				} else {
					toast.error("Unable to open Google sign-in");
				}

				window.setTimeout(() => {
					host.remove();
				}, 5000);
			}
		});
	}, [clientId, onIdToken]);

	return (
		<button
			type="button"
			onClick={handleClick}
			disabled={disabled || !gisReady}
			className="auth-social__button"
		>
			<FcGoogle size={20} />
			<span>Continue with Google</span>
			{disabled ? <AiOutlineLoading3Quarters size={18} className="animate-spin" /> : null}
		</button>
	);
};

/**
 * @param {{ dividerPlacement?: "above" | "below" | "none" }} props
 * dividerPlacement="below" when social buttons lead the form (login / register).
 * dividerPlacement="above" keeps the previous “or continue with” placement under the form.
 */
const SocialLoginButtons = ({ dividerPlacement = "above" }) => {
	const { mutateAsync, isLoading } = useSocialLogin();
	const { mutateAsync: resolveConflict, isLoading: isResolving } = useResolveSocialConflict();
	const facebook = useFacebookSdk();
	const googleEnabled = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);
	const [conflict, setConflict] = useState(null);

	const busy = isLoading || isResolving;

	const exchangeGoogleIdToken = async (idToken) => {
		try {
			await mutateAsync({
				provider: "google",
				id_token: idToken,
			});
		} catch (error) {
			if (isUnverifiedEmailHeldConflict(error)) {
				setConflict({
					claimToken: error?.response?.data?.data?.claim_token,
					email: error?.response?.data?.data?.email,
					message: error?.response?.data?.message,
				});
				return;
			}
		}
	};

	const handleFacebookLogin = async () => {
		if (!facebook.enabled) {
			toast.error("Facebook login is not configured");
			return;
		}

		try {
			const accessToken = await facebook.login();
			await mutateAsync({
				provider: "facebook",
				access_token: accessToken,
			});
		} catch (error) {
			if (error?.response) {
				return;
			}
			toast.error(error?.message || "Facebook sign-in failed");
		}
	};

	const handleLink = async ({ email, password }) => {
		if (!conflict?.claimToken) {
			throw new Error("Missing claim token");
		}
		await resolveConflict({
			claim_token: conflict.claimToken,
			resolution: "link",
			email,
			password,
		});
		setConflict(null);
	};

	const handleSeparate = async () => {
		if (!conflict?.claimToken) {
			throw new Error("Missing claim token");
		}
		await resolveConflict({
			claim_token: conflict.claimToken,
			resolution: "separate",
		});
		setConflict(null);
	};

	if (!googleEnabled && !facebook.enabled) {
		return null;
	}

	const divider = (
		<div className="auth-social__divider">
			<div className="flex-1 h-px bg-gray-300" />
			<span className="auth-social__divider-label">or use your email</span>
			<div className="flex-1 h-px bg-gray-300" />
		</div>
	);

	const buttons = (
		<div className="auth-social__buttons">
			{googleEnabled ? (
				<GoogleSocialButton disabled={busy} onIdToken={exchangeGoogleIdToken} />
			) : null}

			{facebook.enabled ? (
				<button
					type="button"
					onClick={handleFacebookLogin}
					disabled={busy || !facebook.ready}
					className="auth-social__button"
				>
					<FaFacebook size={18} className="text-[#1877F2]" />
					<span>Continue with Facebook</span>
					{busy ? <AiOutlineLoading3Quarters size={18} className="animate-spin" /> : null}
				</button>
			) : null}
		</div>
	);

	return (
		<div className="auth-social">
			{dividerPlacement === "above" ? (
				<>
					<div className="auth-social__divider">
						<div className="flex-1 h-px bg-gray-300" />
						<span className="text-xs uppercase tracking-widest text-gray-400 whitespace-nowrap">
							or continue with
						</span>
						<div className="flex-1 h-px bg-gray-300" />
					</div>
					{buttons}
				</>
			) : null}

			{dividerPlacement === "below" ? (
				<>
					{buttons}
					{divider}
				</>
			) : null}

			{dividerPlacement === "none" ? buttons : null}

			<SocialEmailConflictDialog
				visible={Boolean(conflict?.claimToken)}
				message={conflict?.message}
				email={conflict?.email}
				onHide={() => setConflict(null)}
				onLink={handleLink}
				onSeparate={handleSeparate}
				isSubmitting={isResolving}
			/>
		</div>
	);
};

export default SocialLoginButtons;
